/* eslint-disable react/no-multi-comp */
/** @typedef {import('../../shared/HomeownerService/Homeowner').HomeownerTeamMember} ThemeTeamMember */

import BaseButton from '../../../shared/BaseButton/BaseButton';
import BaseModal from '../../shared/BaseModal/BaseModal';
import ExternalAnchorButton from '../../../shared/ExternalAnchorButton';
import FormField from '../../../shared/Forms/Fields/FormField';
import Loading from '../../../shared/Loading';
import ProfileImage from '../../../shared/ProfileImage';
import React, { useEffect, useMemo, useState } from 'react';
import formatPhoneNumberForDisplay from '../../../utils/formatPhoneNumberForDisplay';
import formatWebsiteForDisplay from '../../../utils/formatWebsiteForDisplay';
import useAuth from '../../../Hooks/Shared/useAuth/useAuth';
import useTheme from '../../../Hooks/Shared/useTheme/useTheme';
import * as validators from '../../../shared/validators';
import { getPropertyTeam, updatePropertyTeamMember } from './property-service';

const TEAM_MEMBER_TYPES = [
	{
		allowNotApplicable: false,
		key: 'real-estate-agent',
		label: 'My Real Estate Agent',
		locked: true,
		type: 'real_estate_agent',
	},
	{
		allowNotApplicable: false,
		key: 'mortgage-loan-officer',
		label: 'My Mortgage Loan Officer',
		type: 'mortgage_loan_officer',
	},
	{
		allowNotApplicable: false,
		key: 'insurance-agent',
		label: 'My Insurance Agent',
		type: 'insurance_agent',
	},
	{
		allowNotApplicable: true,
		key: 'property-management-company',
		label: 'My Property Management Company',
		type: 'property_management_company',
	},
	{
		allowNotApplicable: true,
		key: 'home-owners-association',
		label: 'My Home-Owners Association',
		type: 'home_owners_association',
	},
];

const EMPTY_MEMBER = {
	company: '',
	email: '',
	isNotApplicable: false,
	name: '',
	phoneNumber: '',
	website: '',
};

const TEAM_MEMBER_TYPE_BY_KEY = Object.fromEntries(
	TEAM_MEMBER_TYPES.map((definition) => [definition.key, definition.type])
);
const TEAM_MEMBER_PHONE_DIGIT_COUNT = 10;
const VALID_WEBSITE_PROTOCOLS = ['http:', 'https:'];

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function valueOrEmpty(value) {
	return value ?? '';
}

/**
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalize(value) {
	return value?.trim().toLowerCase() ?? '';
}

/**
 * @param {ThemeTeamMember[]} teamList
 * @returns {ThemeTeamMember | null}
 */
function getRealEstateAgent(teamList) {
	return (
		teamList.find((member) =>
			['agent', 'real_estate_agent', 'realtor'].includes(
				normalize(member.occupation)
			)
		) ??
		teamList.find((member) => member.isManager) ??
		teamList[0] ??
		null
	);
}

/**
 * @param {ThemeTeamMember[]} teamList
 * @returns {ThemeTeamMember | null}
 */
function getMortgageLoanOfficer(teamList) {
	return (
		teamList.find((member) =>
			['lender', 'loan_officer', 'mortgage_loan_officer'].includes(
				normalize(member.occupation)
			)
		) ?? null
	);
}

/**
 * @param {ThemeTeamMember} agent
 * @returns {{company: string, email: string, headshotUrl: string | null, name: string, phoneNumber: string, website: string}}
 */
function mapAgentToMember(agent) {
	return {
		company: valueOrEmpty(agent.groupName ?? agent.primaryGroup?.name),
		email: valueOrEmpty(agent.email),
		headshotUrl: agent.headshotUrl || null,
		isNotApplicable: false,
		name: valueOrEmpty(agent.name),
		phoneNumber: valueOrEmpty(agent.phoneNumber),
		website: '',
	};
}

/**
 * @param {Array<typeof EMPTY_MEMBER & {type: string}>} teamMemberList
 * @returns {Record<string, typeof EMPTY_MEMBER>}
 */
function mapTeamMembersByKey(teamMemberList) {
	return Object.fromEntries(
		teamMemberList
			.map((teamMember) => {
				const definition = TEAM_MEMBER_TYPES.find(
					(memberType) => memberType.type === teamMember.type
				);

				return definition ? [definition.key, teamMember] : null;
			})
			.filter(Boolean)
	);
}

/**
 * @param {string} email
 * @returns {string}
 */
function validateTeamMemberEmail(email) {
	return email && !validators.emailRegExp.test(email)
		? 'Email Address must be a valid email.'
		: '';
}

/**
 * @param {string} phoneNumber
 * @returns {string}
 */
function validateTeamMemberPhoneNumber(phoneNumber) {
	const digitCount = phoneNumber.replace(/\D/g, '').length;

	return phoneNumber && digitCount !== TEAM_MEMBER_PHONE_DIGIT_COUNT
		? 'Phone Number must be a valid 10 digit phone number.'
		: '';
}

/**
 * @param {string} website
 * @returns {string}
 */
function validateTeamMemberWebsite(website) {
	if (!website) {
		return '';
	}

	try {
		const url = new URL(website);

		return VALID_WEBSITE_PROTOCOLS.includes(url.protocol)
			? ''
			: 'Website must be a valid URL.';
	} catch (error) {
		return 'Website must be a valid URL.';
	}
}

/**
 * @param {typeof EMPTY_MEMBER} teamMember
 * @returns {string}
 */
function getTeamMemberValidationError(teamMember) {
	return (
		validateTeamMemberEmail(teamMember.email) ||
		validateTeamMemberPhoneNumber(teamMember.phoneNumber) ||
		validateTeamMemberWebsite(teamMember.website)
	);
}

/**
 * @param {{member: typeof EMPTY_MEMBER}} props
 * @returns {JSX.Element}
 */
function TeamContactActions(props) {
	const { email, phoneNumber } = props.member;

	return (
		<div className="flex min-w-20 flex-col items-start gap-2">
			{phoneNumber ? (
				<ExternalAnchorButton
					className="button button-outline button-sm min-w-28 justify-center"
					href={`tel:${phoneNumber}`}
				>
					{'Call'}
				</ExternalAnchorButton>
			) : null}
			{email ? (
				<ExternalAnchorButton
					className="button button-outline button-sm min-w-28 justify-center"
					href={`mailto:${email}`}
				>
					{'Email'}
				</ExternalAnchorButton>
			) : null}
		</div>
	);
}

/**
 * @param {{
 *  isOpen: boolean,
 *  errorMessage: string,
 *  isSaving: boolean,
 *  member: typeof EMPTY_MEMBER,
 *  title: string,
 *  onCancel: () => void,
 *  onBlur: (event: React.FocusEvent<HTMLInputElement>) => void,
 *  onCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
 *  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
 *  onSave: () => void,
 *  useForAllProperties: boolean
 * }} props
 * @returns {JSX.Element | null}
 */
function TeamMemberModal(props) {
	if (!props.isOpen) {
		return null;
	}

	return (
		<BaseModal onClose={props.onCancel}>
			<form
				className="w-[min(42rem,calc(100vw-4rem))]"
				onSubmit={(event) => {
					event.preventDefault();
					props.onSave();
				}}
			>
				<header className="mb-6 pr-6 text-center">
					<h2 className="mb-0 text-ms-5">{props.title}</h2>
				</header>
				<section className="grid gap-4">
					<FormField
						inputName="property-team-name"
						label="Name"
						labelClass="text-grey-900"
					>
						<input
							className="input"
							id="property-team-name"
							name="name"
							onBlur={props.onBlur}
							onChange={props.onChange}
							type="text"
							value={props.member.name}
						/>
					</FormField>
					<FormField
						inputName="property-team-phone"
						label="Phone Number"
						labelClass="text-grey-900"
					>
						<input
							className="input"
							id="property-team-phone"
							name="phoneNumber"
							onBlur={props.onBlur}
							onChange={props.onChange}
							type="tel"
							value={props.member.phoneNumber}
						/>
					</FormField>
					<FormField
						inputName="property-team-email"
						label="Email Address"
						labelClass="text-grey-900"
					>
						<input
							className="input"
							id="property-team-email"
							name="email"
							onBlur={props.onBlur}
							onChange={props.onChange}
							type="email"
							value={props.member.email}
						/>
					</FormField>
					<FormField
						inputName="property-team-company"
						label="Company"
						labelClass="text-grey-900"
					>
						<input
							className="input"
							id="property-team-company"
							name="company"
							onBlur={props.onBlur}
							onChange={props.onChange}
							type="text"
							value={props.member.company}
						/>
					</FormField>
					<FormField
						inputName="property-team-website"
						label="Website"
						labelClass="text-grey-900"
					>
						<input
							className="input"
							id="property-team-website"
							name="website"
							onBlur={props.onBlur}
							onChange={props.onChange}
							type="url"
							value={props.member.website}
						/>
					</FormField>
				</section>
				<label className="form-check mt-4 flex items-start gap-2">
					<input
						checked={props.useForAllProperties}
						className="form-check-input mt-1 flex-none"
						id="property-team-use-all-properties"
						onChange={props.onCheckboxChange}
						type="checkbox"
					/>
					<span className="form-check-label ml-0">
						{'Use this as '}
						{props.title.replace('Add Your ', '')}
						{' for all properties'}
					</span>
				</label>
				{props.errorMessage ? (
					<p className="mt-4 mb-0 text-feedback-error-dark">
						{props.errorMessage}
					</p>
				) : null}
				<footer className="button-group mx-auto mt-6 w-fit">
					<BaseButton
						disabled={props.isSaving}
						onClick={props.onCancel}
						outline={true}
						size="small"
					>
						{'Cancel'}
					</BaseButton>
					<BaseButton
						disabled={props.isSaving}
						size="small"
						type="submit"
					>
						{props.isSaving ? 'Saving...' : 'Save'}
					</BaseButton>
				</footer>
			</form>
		</BaseModal>
	);
}

/**
 * @param {{
 *  definition: typeof TEAM_MEMBER_TYPES[number],
 *  member: typeof EMPTY_MEMBER & {headshotUrl?: string | null} | null,
 *  onAdd: () => void,
 *  onEdit: () => void,
 *  onNotApplicable: () => void
 * }} props
 * @returns {JSX.Element}
 */
function TeamMemberRow(props) {
	const member = props.member;

	function renderNotApplicable() {
		return (
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="mb-0 text-grey-700">{'N/A'}</p>
				<div className="ml-auto">
					<BaseButton onClick={props.onEdit} size="small">
						{'Edit'}
					</BaseButton>
				</div>
			</div>
		);
	}

	function renderMemberDetails() {
		return (
			<div className="flex flex-wrap items-start gap-x-4 gap-y-4">
				<div className="w-10 flex-none">
					<ProfileImage
						altText={`${member.name || 'Team member'}'s headshot`}
						className="avatar avatar-md border-theme-dark"
						headshotUrl={member.headshotUrl}
					/>
				</div>
				<div className="min-w-32 max-w-56 flex-1">
					<p className="mb-0">{member.name}</p>
					<p className="mb-0">{member.company}</p>
				</div>
				<div className="min-w-48 max-w-72 flex-1 break-words">
					<p className="mb-0">{member.phoneNumber}</p>
					{member.email ? (
						<a href={`mailto:${member.email}`}>{member.email}</a>
					) : null}
					{member.website ? (
						<p className="mb-0">{member.website}</p>
					) : null}
				</div>
				<div className="flex-none">
					<TeamContactActions member={member} />
				</div>
				<div className="ml-auto flex-none">
					<BaseButton onClick={props.onEdit} size="small">
						{'Edit'}
					</BaseButton>
				</div>
			</div>
		);
	}

	function renderEmptyActions() {
		return (
			<div className="flex flex-wrap items-center justify-end gap-3">
				<BaseButton onClick={props.onAdd} size="small">
					{'Add'}
				</BaseButton>
				{props.definition.allowNotApplicable ? (
					<BaseButton onClick={props.onNotApplicable} size="small">
						{'N/A'}
					</BaseButton>
				) : null}
			</div>
		);
	}

	return (
		<section className="border-b border-grey-200 py-5 last:border-b-0">
			<h2 className="mb-3 font-bold text-ms-1">
				{props.definition.label}
			</h2>
			{member?.isNotApplicable ? renderNotApplicable() : null}
			{member && !member.isNotApplicable ? renderMemberDetails() : null}
			{!member ? renderEmptyActions() : null}
		</section>
	);
}

/**
 * @param {{match?: {params?: {propertyId?: string}}}} props
 * @returns {JSX.Element}
 */
// eslint-disable-next-line complexity -- Coordinates property route, API state, modal state, and save handlers.
export default function PropertyTeam(props) {
	const auth = useAuth();
	const { teamList } = useTheme();
	const propertyId = props.match?.params?.propertyId;
	const realEstateAgent = useMemo(
		() => getRealEstateAgent(teamList ?? []),
		[teamList]
	);
	const mortgageLoanOfficer = useMemo(
		() => getMortgageLoanOfficer(teamList ?? []),
		[teamList]
	);
	const [members, setMembers] = useState({});
	const [activeType, setActiveType] = useState(null);
	const [draft, setDraft] = useState(EMPTY_MEMBER);
	const [errorMessage, setErrorMessage] = useState('');
	const [isGettingTeam, setIsGettingTeam] = useState(false);
	const [isSavingTeamMember, setIsSavingTeamMember] = useState(false);
	const [modalErrorMessage, setModalErrorMessage] = useState('');
	const [teamPropertyId, setTeamPropertyId] = useState(null);
	const [useForAllProperties, setUseForAllProperties] = useState(false);

	const teamMembers = {
		...(realEstateAgent
			? { 'real-estate-agent': mapAgentToMember(realEstateAgent) }
			: {}),
		...(mortgageLoanOfficer
			? { 'mortgage-loan-officer': mapAgentToMember(mortgageLoanOfficer) }
			: {}),
		...members,
	};
	const activeDefinition = TEAM_MEMBER_TYPES.find(
		(type) => type.key === activeType
	);
	const modalTitle = activeDefinition
		? `Add Your ${activeDefinition.label.replace('My ', '')}`
		: '';
	const isTeamLoading = Boolean(
		auth.accessToken &&
			propertyId &&
			(isGettingTeam || teamPropertyId !== propertyId)
	);

	useEffect(() => {
		if (!auth.accessToken || !propertyId) {
			setMembers({});
			setTeamPropertyId(null);
			return undefined;
		}

		let isMounted = true;

		async function loadPropertyTeam() {
			setMembers({});
			setTeamPropertyId(null);
			setIsGettingTeam(true);
			setErrorMessage('');

			try {
				const teamMemberList = await getPropertyTeam(
					propertyId,
					auth.accessToken
				);

				if (isMounted) {
					setMembers(mapTeamMembersByKey(teamMemberList));
					setTeamPropertyId(propertyId);
				}
			} catch (error) {
				if (isMounted) {
					setErrorMessage(error.message);
					setTeamPropertyId(propertyId);
				}
			} finally {
				if (isMounted) {
					setIsGettingTeam(false);
				}
			}
		}

		loadPropertyTeam();

		return () => {
			isMounted = false;
		};
	}, [auth.accessToken, propertyId]);

	/**
	 * @param {string} key
	 * @returns {void}
	 */
	function openModal(key) {
		const currentMember = teamMembers[key];

		setActiveType(key);
		setDraft(
			currentMember?.isNotApplicable
				? EMPTY_MEMBER
				: currentMember ?? EMPTY_MEMBER
		);
		setModalErrorMessage('');
		setUseForAllProperties(false);
	}

	/**
	 * @param {React.ChangeEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	function handleDraftChange(event) {
		setModalErrorMessage('');
		setDraft((currentDraft) => ({
			...currentDraft,
			[event.target.name]: event.target.value,
		}));
	}

	/**
	 * @param {React.FocusEvent<HTMLInputElement>} event
	 * @returns {void}
	 */
	function handleDraftBlur(event) {
		const { name, value } = event.target;

		if (name === 'phoneNumber') {
			if (
				value &&
				value.replace(/\D/g, '').length !==
					TEAM_MEMBER_PHONE_DIGIT_COUNT
			) {
				return;
			}

			setDraft((currentDraft) => ({
				...currentDraft,
				phoneNumber: formatPhoneNumberForDisplay(value),
			}));
		}

		if (name === 'website') {
			setDraft((currentDraft) => ({
				...currentDraft,
				website: formatWebsiteForDisplay(value),
			}));
		}
	}

	async function saveTeamMember(typeKey, teamMember, options = {}) {
		const type = TEAM_MEMBER_TYPE_BY_KEY[typeKey];

		if (!auth.accessToken || !propertyId || !type) {
			return;
		}

		setIsSavingTeamMember(true);
		setErrorMessage('');
		setModalErrorMessage('');

		try {
			const savedTeamMember = await updatePropertyTeamMember(
				propertyId,
				type,
				teamMember,
				auth.accessToken,
				options
			);

			setMembers((currentMembers) => ({
				...currentMembers,
				[typeKey]: savedTeamMember,
			}));
			setActiveType(null);
		} catch (error) {
			if (activeType) {
				setModalErrorMessage(error.message);
			} else {
				setErrorMessage(error.message);
			}
		} finally {
			setIsSavingTeamMember(false);
		}
	}

	function saveDraft() {
		if (!activeType) {
			return;
		}

		const validationError = getTeamMemberValidationError(draft);

		if (validationError) {
			setModalErrorMessage(validationError);
			return;
		}

		saveTeamMember(activeType, draft, {
			applyToAllProperties: useForAllProperties,
		});
	}

	function saveNotApplicable(typeKey) {
		saveTeamMember(typeKey, {
			...EMPTY_MEMBER,
			isNotApplicable: true,
		});
	}

	function saveLocalDraft() {
		if (!activeType) {
			return;
		}

		const validationError = getTeamMemberValidationError(draft);

		if (validationError) {
			setModalErrorMessage(validationError);
			return;
		}

		setMembers((currentMembers) => ({
			...currentMembers,
			[activeType]: draft,
		}));
		setActiveType(null);
	}

	return (
		<section>
			{errorMessage ? (
				<p className="text-feedback-error-dark">{errorMessage}</p>
			) : null}
			{isTeamLoading ? (
				<Loading />
			) : (
				<section className="bg-white">
					{TEAM_MEMBER_TYPES.map((definition) => (
						<TeamMemberRow
							definition={definition}
							key={definition.key}
							member={teamMembers[definition.key] ?? null}
							onAdd={() => openModal(definition.key)}
							onEdit={() => openModal(definition.key)}
							onNotApplicable={() =>
								saveNotApplicable(definition.key)
							}
						/>
					))}
				</section>
			)}
			<TeamMemberModal
				errorMessage={modalErrorMessage}
				isOpen={Boolean(activeDefinition)}
				isSaving={isSavingTeamMember}
				member={draft}
				onBlur={handleDraftBlur}
				onCancel={() => setActiveType(null)}
				onChange={handleDraftChange}
				onCheckboxChange={(event) =>
					setUseForAllProperties(event.target.checked)
				}
				onSave={
					propertyId && auth.accessToken ? saveDraft : saveLocalDraft
				}
				title={modalTitle}
				useForAllProperties={useForAllProperties}
			/>
			{isSavingTeamMember ? <p>{'Saving team member...'}</p> : null}
		</section>
	);
}
